/**
 * SOFSEK — Mapping Dictionary Manager (§15). File-based ingestion reuses the
 * platform's UPP import mapping engine; this page is the SOFSEK-specific piece
 * — binding a per-application JSON API payload field to a lookup-coded
 * tracking token. Dual-panel: applications left, field map right.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedDelete } from '../../lib/routed_writes';
import { hasAccess, type AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { PersonalizationProvider, PersonalizationToggle } from '../../components/personalization';

const PAGE_CODE = 'SFK.API_FIELD_MAP.LIST';

interface CodeOpt { code: string; name: string; }
interface FieldMapRow { map_id: number; application_id: number; source_field: string; tracking_token_code: string; }

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

function ApiFieldMapInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const [apps, setApps] = useState<CodeOpt[]>([]);
  const [tokens, setTokens] = useState<CodeOpt[]>([]);
  const [maps, setMaps] = useState<FieldMapRow[]>([]);
  const [selectedApp, setSelectedApp] = useState<CodeOpt | null>(null);
  const [appFilter, setAppFilter] = useState('');
  const [newField, setNewField] = useState('');
  const [newToken, setNewToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [appRes, tokenRes, mapRes] = await Promise.all([
        apiDb.from('csm_application_master').select('application_id, csm_application_master_tl ( locale_code, display_text )'),
        apiDb.from('sfk_tracking_token').select('tracking_token_code, sfk_tracking_token_tl ( locale_code, display_text )'),
        apiDb.from('sfk_api_field_map').select('map_id, application_id, source_field, tracking_token_code'),
      ]);
      if (appRes.error) throw appRes.error;
      if (tokenRes.error) throw tokenRes.error;
      if (mapRes.error) throw mapRes.error;
      const appOpts = ((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: String(r.application_id), name: tlName(r.csm_application_master_tl, String(r.application_id)),
      }));
      setApps(appOpts);
      setTokens(((tokenRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: r.tracking_token_code as string, name: tlName(r.sfk_tracking_token_tl, r.tracking_token_code as string),
      })));
      setMaps((mapRes.data ?? []) as unknown as FieldMapRow[]);
      if (!selectedApp && appOpts.length > 0) setSelectedApp(appOpts[0]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, []);

  const filteredApps = useMemo(() => {
    const q = appFilter.trim().toLowerCase();
    return apps.filter(a => !q || a.name.toLowerCase().includes(q));
  }, [apps, appFilter]);

  const appMaps = useMemo(() =>
    selectedApp ? maps.filter(m => String(m.application_id) === selectedApp.code) : [],
  [maps, selectedApp]);

  const resolveNameForToken = (code: string) => tokens.find(x => x.code === code)?.name ?? code;

  const addMap = async () => {
    if (!selectedApp || !newField.trim() || !newToken) return;
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedInsert(session, 'sfk_api_field_map', {
        application_id: Number(selectedApp.code),
        source_field: newField.trim(),
        tracking_token_code: newToken,
      });
      if (e) throw new Error(e.message);
      setNewField(''); setNewToken('');
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const removeMap = async (mapId: number) => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'sfk_api_field_map', 'map_id', mapId);
      if (e) throw new Error(e.message);
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-hidden p-6">
        <PageHeader
          title={t('sfk.api_field_map.title', 'Mapping Dictionary Manager')}
          description={t('sfk.api_field_map.subtitle', 'Map each application’s incoming source fields to their tracking tokens.')}
          actions={
            <>
              {busy && <span className="self-center text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </>
          }
        />
        <StatStrip stats={[{ value: apps.length, label: t('sfk.api_field_map.stat.apps', 'Applications') }, { value: maps.length, label: t('sfk.api_field_map.stat.maps', 'Mappings') }]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex w-72 shrink-0 flex-col border-r border-csm-border bg-white">
          <div className="flex items-center gap-2 border-b border-csm-border p-2">
            <input value={appFilter} onChange={e => setAppFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')} className="h-7 w-full rounded border border-csm-border px-2 text-csm-sm" />
            <ClearFiltersButton active={appFilter.trim() !== ''} onClear={() => setAppFilter('')} />
          </div>
          <div className="flex-1 overflow-auto">
            {filteredApps.map(a => (
              <button key={a.code} onClick={() => setSelectedApp(a)}
                className={`block w-full border-b border-csm-border-soft px-3 py-2 text-left text-csm-sm hover:bg-csm-surface-alt ${selectedApp?.code === a.code ? 'bg-csm-surface-alt font-medium' : ''}`}>
                {a.name}
              </button>
            ))}
            {filteredApps.length === 0 && <div className="p-6"><EmptyState title={t('common.no_records', 'No records')} /></div>}
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden">
          {!selectedApp ? (
            <div className="flex flex-1 items-center justify-center text-csm-sm text-csm-watermark">{t('sfk.api_field_map.select_prompt', 'Select an application to map its incoming fields.')}</div>
          ) : (
            <>
              <div className="flex items-center gap-2 border-b border-csm-border p-2">
                <span className="text-csm-sm font-medium text-csm-ink-soft">{selectedApp.name}</span>
                <input value={newField} onChange={e => setNewField(e.target.value)} placeholder={t('sfk.api_field_map.source_field', 'Source field (e.g. user_id)')} className="ml-2 h-7 w-56 rounded border border-csm-border px-2 text-csm-sm" />
                <select value={newToken} onChange={e => setNewToken(e.target.value)} className="h-7 w-56 rounded border border-csm-border bg-white px-2 text-csm-sm">
                  <option value="">{t('sfk.api_field_map.select_token', '— tracking token —')}</option>
                  {tokens.map(tok => <option key={tok.code} value={tok.code}>{tok.name}</option>)}
                </select>
                {canAdd && (<button onClick={() => void addMap()} disabled={!newField.trim() || !newToken} className="h-7 rounded border border-primary bg-primary px-2 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">+ {t('common.add', 'Add')}</button>)}
              </div>
              <div className="flex-1 overflow-auto p-2">
                {appMaps.length === 0 && <EmptyState title={t('common.no_records', 'No records')} />}
                <div className="flex flex-col gap-0.5">
                  {appMaps.map(m => (
                    <div key={m.map_id} className="flex items-center gap-2 rounded px-2 py-1 text-csm-sm hover:bg-csm-surface-alt">
                      <span className="w-56 font-mono text-csm-xs">{m.source_field}</span>
                      <span className="text-csm-watermark">→</span>
                      <span className="flex-1">{resolveNameForToken(m.tracking_token_code)}</span>
                      {canDelete && (<button onClick={() => void removeMap(m.map_id)} className="text-csm-xs text-csm-danger">{t('common.remove', 'Remove')}</button>)}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        </ContentCard>
      </div>
    </div>
  );
}

export function SfkApiFieldMapPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <ApiFieldMapInner session={session} />
    </PersonalizationProvider>
  );
}
